import classnames from "classnames";
import { ReactElement, useState } from "react";
import { Text } from "@/elements/text/text";
import { Icon } from "@/elements/icon/icon";
import { i18n } from "@/model/i18n";

const STEPS = [
  {
    title: i18n.t("pages.advisor.risk_management_slider.step_1.title"),
    altText: i18n.t("pages.advisor.risk_management_slider.step_1.alt_text"),
    desc: i18n.t("pages.advisor.risk_management_slider.step_1.description"),
  },
  {
    title: i18n.t("pages.advisor.risk_management_slider.step_2.title"),
    altText: i18n.t("pages.advisor.risk_management_slider.step_2.alt_text"),
    desc: i18n.t("pages.advisor.risk_management_slider.step_2.description"),
  },
  {
    title: i18n.t("pages.advisor.risk_management_slider.step_3.title"),
    altText: i18n.t("pages.advisor.risk_management_slider.step_3.alt_text"),
    desc: i18n.t("pages.advisor.risk_management_slider.step_3.description"),
  },
  {
    title: i18n.t("pages.advisor.risk_management_slider.step_4.title"),
    altText: i18n.t("pages.advisor.risk_management_slider.step_4.alt_text"),
    desc: i18n.t("pages.advisor.risk_management_slider.step_4.description"),
  },
] as const;

export function AdvisorRiskManagementSlider(): ReactElement {
  const [activeItem, setActiveItem] = useState(0);
  const disabledLeft = activeItem === 0;
  const disabledRight = activeItem === STEPS.length - 1;

  return (
    <section
      className="advisor-risk-management-slider"
      aria-label={i18n.t("pages.advisor.risk_management_slider.alt_text", {
        current: STEPS[activeItem].altText,
      })}
    >
      <div className="advisor-risk-management-slider__top">
        <ul className="advisor-risk-management-slider__list">
          {STEPS.map((step, index) => {
            const isActive = activeItem === index;

            return (
              <li
                key={step.title}
                className={classnames(
                  "advisor-risk-management-slider__item",
                  `advisor-risk-management-slider__step_${index + 1}`,
                  {
                    "advisor-risk-management-slider__item--active": isActive,
                  },
                )}
                onClick={() => {
                  setActiveItem((step) => {
                    if (step === STEPS.length - 1) {
                      return 0;
                    }
                    return step + 1;
                  });
                }}
                onTouchEnd={() => {
                  setActiveItem((step) => {
                    if (step === STEPS.length - 1) {
                      return 0;
                    }
                    return step + 1;
                  });
                }}
              >
                <div
                  className="advisor-risk-management-slider__image"
                  role="img"
                  aria-label={step.altText}
                ></div>
              </li>
            );
          })}
        </ul>
      </div>
      <div className="advisor-risk-management-slider__bottom">
        <button
          className={classnames(
            "advisor-risk-management-slider__arrow",
            "advisor-risk-management-slider__arrow--left",
            {
              "advisor-risk-management-slider__arrow--disabled": disabledRight,
            },
          )}
          onClick={() => {
            setActiveItem((item) => {
              return Math.max(item - 1, 0);
            });
          }}
        >
          <Icon
            name="arrow-slider-left"
            size={42}
            color={disabledLeft ? "disabled-on-default" : "accent-tinted"}
          />
        </button>
        <ul className="advisor-risk-management-slider__contents">
          {STEPS.map((step, index) => {
            const isActive = activeItem === index;

            return (
              <li
                key={step.title}
                className={classnames(
                  "advisor-risk-management-slider__content",
                  {
                    "advisor-risk-management-slider__content--active": isActive,
                  },
                )}
              >
                <Text
                  htmlElement="h4"
                  type="sh1-xbold"
                  color="neutral-on-surface"
                >
                  {step.title}
                </Text>
                <Text
                  htmlElement="p"
                  type="body-regular"
                  color="neutral-on-surface"
                >
                  {step.desc}
                </Text>
              </li>
            );
          })}
        </ul>
        <button
          className={classnames(
            "advisor-risk-management-slider__arrow",
            "advisor-risk-management-slider__arrow--left",
            {
              "advisor-risk-management-slider__arrow--disabled": disabledRight,
            },
          )}
          onClick={() => {
            setActiveItem((item) => {
              return Math.min(item + 1, STEPS.length - 1);
            });
          }}
        >
          <Icon
            name="arrow-slider-right"
            size={42}
            color={disabledRight ? "disabled-on-default" : "accent-tinted"}
          />
        </button>
        <ul className="advisor-risk-management-slider__bullets">
          {STEPS.map((step, index) => {
            const isActive = activeItem === index;

            return (
              <li
                key={step.title}
                className={classnames(
                  "advisor-risk-management-slider__bullet",
                  {
                    "advisor-risk-management-slider__bullet--active": isActive,
                  },
                )}
                onClick={() => setActiveItem(index)}
              ></li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}
